import React, { useState } from 'react';
import { Card, Table, Form, Input, Select, Button, Space, Badge, Typography, Popconfirm, message, Tag } from 'antd';
import { useTableRequest } from '@/hooks';
import useAuthButton from '@/hooks/useAuthButton';
import {
  getWorkOrderList,
  deleteWorkOrder,
  WorkOrderQueryResponse,
} from '@/api/workorder';
import { getDevice } from '@/api';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import CreateModal from './CreateModal';
import AssignModal from './AssignModal';
import CompleteModal from './CompleteModal';

const { Text } = Typography;

const statusMap: Record<string, { color: string; label: string }> = {
  pending: { color: 'orange', label: '待处理' },
  assigned: { color: 'blue', label: '已分配' },
  processing: { color: 'cyan', label: '处理中' },
  completed: { color: 'green', label: '已完成' },
  cancelled: { color: 'gray', label: '已取消' },
};

const priorityMap: Record<number, { color: string; label: string }> = {
  1: { color: 'red', label: '紧急' },
  2: { color: 'orange', label: '高' },
  3: { color: 'blue', label: '中' },
  4: { color: 'default', label: '低' },
};

const orderTypeMap: Record<string, string> = {
  install: '安装',
  recharge: '充值',
  replace_core: '换芯',
  filter_change: '换芯',
  remove: '撤机',
  repair: '维修',
  inspect: '巡检',
  other: '其他',
  '充值': '充值',
  '扣款': '扣款',
};

const WorkOrderList: React.FC = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const { checkPermissionDisabled } = useAuthButton();

  const [createOpen, setCreateOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<WorkOrderQueryResponse | null>(null);

  const { data: deviceData } = useRequest(getDevice, { defaultParams: [{ isAll: true }] });

  const { loading, dataSource, pagination, form, reload, search, reset } = useTableRequest<WorkOrderQueryResponse>({
    onRequest: (params) => {
      return getWorkOrderList(params);
    },
  });

  const handleDelete = async (id: string) => {
    const resp = await deleteWorkOrder(id);
    if (resp.success) {
      messageApi.success('删除成功');
      reload();
    } else {
      messageApi.error(resp.errorMsg || '删除失败');
    }
  };

  const openAssign = (record: WorkOrderQueryResponse) => {
    setCurrentRecord(record);
    setAssignOpen(true);
  };

  const openComplete = (record: WorkOrderQueryResponse) => {
    setCurrentRecord(record);
    setCompleteOpen(true);
  };

  return (
    <>
      {contextHolder}
      <Form layout="inline" className="ml20 mt20" form={form}>
        <Form.Item key="orderType" name="orderType">
          <Select
            placeholder="工单类型"
            style={{ width: 120 }}
            allowClear
            options={[
                { value: '', label: '全部' },
                { value: 'install', label: '安装' },
                { value: 'recharge', label: '充值' },
                { value: 'replace_core', label: '换芯' },
                { value: 'remove', label: '撤机' },
              ]}
          />
        </Form.Item>
        <Form.Item key="status" name="status">
          <Select
            placeholder="工单状态"
            style={{ width: 120 }}
            allowClear
            options={[
              { value: '', label: '全部' },
              { value: 'pending', label: '待处理' },
              { value: 'assigned', label: '已分配' },
              { value: 'processing', label: '处理中' },
              { value: 'completed', label: '已完成' },
              { value: 'cancelled', label: '已取消' },
            ]}
          />
        </Form.Item>
        <Form.Item key="deviceId" name="deviceId">
          <Select
            options={deviceData?.result?.list || []}
            allowClear
            showSearch
            optionFilterProp="name"
            placeholder="请选择设备"
            style={{ width: 160 }}
            fieldNames={{ label: 'name', value: 'id' }}
          />
        </Form.Item>
        <Form.Item key="orderNo" name="orderNo">
          <Input placeholder="工单编号" allowClear style={{ width: 160 }} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" onClick={search}>
            查询
          </Button>
        </Form.Item>
        <Form.Item>
          <Button type="default" onClick={reset}>
            重置
          </Button>
        </Form.Item>
        <Form.Item>
          <Button type="primary" onClick={() => setCreateOpen(true)} disabled={checkPermissionDisabled('workorder:add')}>
            新建工单
          </Button>
        </Form.Item>
      </Form>

      <Card className="m20" size="small">
        <Table
          loading={loading}
          dataSource={dataSource}
          pagination={pagination}
          rowKey="id"
          size="small"
          columns={[
            {
              title: '工单编号',
              key: 'orderNo',
              dataIndex: 'orderNo',
              width: 160,
              render: (value, record) => (
                <Typography.Link
                  onClick={() => {
                    window.open(`/workorder/detail/${record.id}`, '_blank');
                  }}
                >
                  {value || '-'}
                </Typography.Link>
              ),
            },
            {
              title: '工单类型',
              key: 'orderType',
              dataIndex: 'orderType',
              width: 80,
              render: (value) => orderTypeMap[value] || value || '-',
            },
            {
              title: '设备名称',
              key: 'deviceName',
              dataIndex: 'deviceName',
              width: 120,
              render: (value) => value || '-',
            },
            {
              title: '客户',
              key: 'customerName',
              dataIndex: 'customerName',
              width: 80,
              render: (value) => value || '-',
            },
            {
              title: '服务商',
              key: 'providerName',
              dataIndex: 'providerName',
              width: 80,
              render: (value) => value || '-',
            },
            {
              title: '优先级',
              key: 'priority',
              dataIndex: 'priority',
              width: 70,
              render: (value) => {
                const p = priorityMap[value];
                return p ? <Tag color={p.color}>{p.label}</Tag> : '-';
              },
            },
            {
              title: '状态',
              key: 'status',
              dataIndex: 'status',
              width: 80,
              render: (value) => {
                const s = statusMap[value];
                return s ? (
                  <>
                    <Badge color={s.color} />
                    <Text className="ml5">{s.label}</Text>
                  </>
                ) : (
                  value || '-'
                );
              },
            },
            {
              title: '描述',
              key: 'description',
              dataIndex: 'description',
              ellipsis: true,
              width: 150,
              render: (value) => value || '-',
            },
            {
              title: '创建时间',
              key: 'created',
              dataIndex: 'created',
              width: 150,
              render: (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-'),
            },
            {
              title: '操作',
              key: 'action',
              width: 180,
              render: (_, record) => {
                const userInfo = JSON.parse(localStorage.getItem('$UserInfo$') || '{}');
                const isProvider = userInfo.roleKey === 'provider';
                // 已绑定服务商的工单可直接完成（无需先分配）
                const hasProvider = !!record.providerId;
                const canComplete = isProvider
                  ? (record.status === 'pending' || record.status === 'assigned' || record.status === 'processing')
                  : (hasProvider && (record.status === 'pending' || record.status === 'assigned' || record.status === 'processing'))
                    || (!hasProvider && (record.status === 'assigned' || record.status === 'processing'));
                // 已绑定服务商的工单不显示分配按钮
                const showAssign = !isProvider && record.status === 'pending' && !record.providerId;
                return (
                  <Space split={<Text type="secondary">/</Text>}>
                    {showAssign && (
                      <Typography.Link onClick={() => openAssign(record)}>分配</Typography.Link>
                    )}
                    {canComplete && (
                      <Typography.Link onClick={() => openComplete(record)}>完成</Typography.Link>
                    )}
                    {!isProvider && (
                      <Popconfirm
                        title="确定删除此工单？"
                        okText="确定"
                        cancelText="取消"
                        onConfirm={() => handleDelete(record.id)}
                      >
                        <Typography.Link type="danger">删除</Typography.Link>
                      </Popconfirm>
                    )}
                  </Space>
                );
              },
            },
          ]}
        />
      </Card>

      <CreateModal open={createOpen} onClose={() => setCreateOpen(false)} onSuccess={() => { reload(); setCreateOpen(false); }} />

      {currentRecord && (
        <AssignModal
          open={assignOpen}
          record={currentRecord}
          onClose={() => { setAssignOpen(false); setCurrentRecord(null); }}
          onSuccess={() => { reload(); setAssignOpen(false); setCurrentRecord(null); }}
        />
      )}

      {currentRecord && (
        <CompleteModal
          open={completeOpen}
          record={currentRecord}
          onClose={() => { setCompleteOpen(false); setCurrentRecord(null); }}
          onSuccess={() => { reload(); setCompleteOpen(false); setCurrentRecord(null); }}
        />
      )}
    </>
  );
};

export default WorkOrderList;
